<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import Section from "../internal/Section.vue";
import EditableField from "../fields/EditableField.vue";
import ReadonlyField from "../fields/ReadonlyField.vue";
import { Switch } from "@/shared/ui/switch";

const { t } = useI18n();

defineProps({
  data: { type: Object, required: true },
  editMode: { type: Boolean, default: false },
  isOwn: { type: Boolean, default: false },
});

const emit = defineEmits(["update"]);
function setField(key, value) {
  emit("update", key, value);
}

const STATUS_LABELS = {
  ACTIVE: "Действующий",
  SUSPENDED: "Приостановлен",
  EXCLUDED: "Исключён",
};
const STATUSES = computed(() =>
  Object.entries(STATUS_LABELS).map(([value, label]) => ({
    value,
    label: t(label),
  }))
);
function statusLabel(code) {
  return STATUS_LABELS[code] || code || "Не указано";
}
</script>

<template>
  <div class="flex flex-col">
    <Section
      icon="sli-system"
      :title="$t('Системная информация')"
      :sub="$t('Регистрация, доступ и статус в системе')"
    >
      <div class="grid grid-cols-2 gap-x-5 gap-y-3.5 max-sm:grid-cols-1">
        <div class="flex flex-col gap-1.5">
          <span class="text-[12.5px] text-muted-foreground">{{
            $t("Разрешён доступ")
          }}</span>
          <div class="flex items-center gap-2.5 px-2.5 py-1.5 min-h-[34px]">
            <Switch :model-value="!!data.access_allowed" disabled />
            <span
              class="text-sm font-medium"
              :class="
                data.access_allowed ? 'text-green-600' : 'text-muted-foreground'
              "
            >
              {{ data.access_allowed ? $t("Включено") : $t("Отключено") }}
            </span>
          </div>
        </div>

        <template v-if="editMode">
          <EditableField
            :label="$t('Дата учётной регистрации')"
            type="date"
            size="sm"
            required
            :model-value="data.registration_date"
            @update:model-value="setField('registration_date', $event)"
          />
          <EditableField
            :label="$t('Статус')"
            required
            :model-value="data.status"
            :options="STATUSES"
            @update:model-value="setField('status', $event)"
          />
          <EditableField
            :label="$t('Дата исключения из палаты')"
            type="date"
            size="sm"
            :model-value="data.exclusion_date"
            @update:model-value="setField('exclusion_date', $event)"
          />
        </template>
        <template v-else>
          <ReadonlyField
            :label="$t('Дата учётной регистрации')"
            :value="data.registration_date"
            type="date"
            size="sm"
          />
          <ReadonlyField
            :label="$t('Статус')"
            :value="$t(statusLabel(data.status))"
          />
          <ReadonlyField
            :label="$t('Дата исключения из палаты')"
            :value="data.exclusion_date"
            type="date"
            size="sm"
          />
        </template>
      </div>
    </Section>

    <Section
      icon="sli-wallet"
      :title="$t('Банковские реквизиты')"
      :sub="$t('Реквизиты для расчётов')"
    >
      <div class="grid grid-cols-2 gap-x-5 gap-y-3.5 max-sm:grid-cols-1">
        <template v-if="editMode">
          <EditableField
            :label="$t('Банк (рус)')"
            :model-value="data.bank_name"
            @update:model-value="setField('bank_name', $event)"
          />
          <EditableField
            :label="$t('Банк (қаз)')"
            :model-value="data.bank_name_kz"
            @update:model-value="setField('bank_name_kz', $event)"
          />
          <EditableField
            :label="$t('ИИК')"
            :model-value="data.iik"
            @update:model-value="setField('iik', $event)"
          />
          <EditableField
            :label="$t('БИК')"
            :model-value="data.bik"
            @update:model-value="setField('bik', $event)"
          />
          <EditableField
            :label="$t('КБе')"
            type="number"
            :model-value="data.kbe"
            @update:model-value="setField('kbe', $event)"
          />
        </template>
        <template v-else>
          <ReadonlyField :label="$t('Банк (рус)')" :value="data.bank_name" />
          <ReadonlyField :label="$t('Банк (қаз)')" :value="data.bank_name_kz" />
          <ReadonlyField :label="$t('ИИК')" :value="data.iik" />
          <ReadonlyField :label="$t('БИК')" :value="data.bik" />
          <ReadonlyField :label="$t('КБе')" :value="data.kbe" />
        </template>
      </div>
    </Section>
  </div>
</template>
